/**
 * Locale tag to hand to `Intl.*` / ICU formatters, as opposed to the UI
 * locale used for catalog lookup and text direction.
 *
 * Arabic-language locales pin Western (Latin) digits via the `-u-nu-latn`
 * Unicode extension, so numbers, currency amounts and dates never render in
 * Arabic-Indic digits regardless of the runtime's CLDR defaults (ar-EG and
 * ar-SA default to Arabic-Indic; ar-AE has flipped between releases).
 * Every other locale is returned unchanged, and an explicit `-u-nu-` choice
 * already present in the tag is respected.
 *
 * A BCP 47 private-use section (`-x-…`) must stay last in the tag, so the
 * numbering extension is always inserted before it, never after.
 */
export function formattingLocale(locale: string): string {
  const privateUse = /-x-/i.exec(locale);
  const head = privateUse ? locale.slice(0, privateUse.index) : locale;
  const tail = privateUse ? locale.slice(privateUse.index) : '';
  return pinLatinDigits(head) + tail;
}

function pinLatinDigits(tag: string): string {
  const language = tag.split('-')[0]?.toLowerCase();
  if (language !== 'ar') return tag;
  try {
    const parsed = new Intl.Locale(tag);
    // An explicit -u-nu- choice (even a non-Latin one) wins.
    if (parsed.numberingSystem !== undefined) return tag;
    return new Intl.Locale(parsed, { numberingSystem: 'latn' }).toString();
  } catch {
    // Malformed tag: fall back to string surgery so the caller still gets a
    // best-effort tag (Intl.* will then raise its own RangeError if it is
    // truly invalid, exactly as it would have without this helper).
    if (/-u(-[a-z0-9]{2,8})*-nu-/i.test(tag)) return tag;
    return /-u-/i.test(tag) ? `${tag}-nu-latn` : `${tag}-u-nu-latn`;
  }
}
